<!--
InfoTip — the ⓘ next to a label, a column header or a highlight number
(skill `self-explaining-ui`).

  <InfoTip :explanation="EX.effort" :label="t('Esforço')" test-id="chamados-info-esforco" />

Hover or keyboard focus opens the Explanation; a TAP opens it too (v-explain
`.tap`): on a touch screen hover does not exist, and this icon is the only way
to reach the text (E2). The click never bubbles — an ⓘ inside a sortable column
header must not sort the column.

`label` is the thing being explained: it titles the card. The button's
accessible name is the whole explanation as text. Nothing renders without an
explanation.
-->

<template>
  <button
    v-if="explanation"
    v-explain.tap.top="titled"
    type="button"
    class="info-tip"
    tabindex="0"
    :aria-label="ariaLabel"
    :data-testid="testId || undefined"
  >
    <i class="pi pi-info-circle" aria-hidden="true"></i>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { explanationToText } from '../explanations/render';
import type { Explanation, ExplanationInput } from '../explanations/types';
import { vExplain } from '../explanations/vExplain';

defineOptions({ name: 'InfoTip' });

const props = withDefaults(defineProps<{
  explanation?: ExplanationInput;
  /** What is being explained — the card title and the accessible name. */
  label?: string;
  testId?: string;
}>(), {
  explanation: null,
  label: '',
  testId: '',
});

const titled = computed((): Explanation | null =>
  props.explanation ? { ...props.explanation, title: props.explanation.title ?? (props.label || undefined) } : null,
);

/** The whole explanation as text: what the card shows is what a screen reader reads. */
const ariaLabel = computed((): string =>
  titled.value ? explanationToText(titled.value) : '',
);
</script>

<style scoped>
.info-tip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--app-text-secondary-color);
  font-size: 0.85rem;
  line-height: 1;
  vertical-align: middle;
  cursor: help;
}

.info-tip:hover {
  color: var(--app-text-color);
}

.info-tip:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 1px;
}

/* A bigger hit area on touch screens, without growing the icon. */
@media (pointer: coarse) {
  .info-tip {
    width: 2rem;
    height: 2rem;
    margin: -0.375rem;
  }
}
</style>
